import React, { useEffect, useState } from 'react';
import { useMedia } from '../context/MediaContext';
import {
  Play, Pause, X, CaretDown, CornersOut, SkipBack, SkipForward,
  SpeakerHigh, SpeakerSlash, VideoCamera, Television, Lightning, CornersIn
} from '@phosphor-icons/react';
import { isYouTubeUrl, getYouTubeEmbedUrl } from '../utils/mediaUtils';

export const MediaPlayer: React.FC = () => {
  const {
    currentEpisode, isPlaying, toggleMinimize, pause, resume, progress, duration,
    closePlayer, isMinimized, seek, mediaRef, playbackRate, changePlaybackRate,
    volume, changeVolume
  } = useMedia();

  const [isCinematic, setIsCinematic] = useState(true);
  const [isHoveringVideo, setIsHoveringVideo] = useState(false);
  const [showRateMenu, setShowRateMenu] = useState(false);
  const [downloadState, setDownloadState] = useState<'idle' | 'downloading' | 'done' | 'error'>('idle');
  const [forceAudioOnly, setForceAudioOnly] = useState(false);

  const MEDIA_CACHE_NAME = 'nidastudio-audio-v1';

  useEffect(() => {
    if (currentEpisode && mediaRef.current && isPlaying) {
      mediaRef.current.play().catch(e => console.log("Autoplay prevented", e));
    }
  }, [currentEpisode]);

  useEffect(() => {
    setForceAudioOnly(false);
  }, [currentEpisode?.id]);

  const canListenOnly = currentEpisode?.mediaType === 'video' && !!currentEpisode?.audioUrl;
  const isVideo = currentEpisode?.mediaType === 'video' && !forceAudioOnly;
  const mediaUrl = isVideo ? currentEpisode?.videoUrl : currentEpisode?.audioUrl;
  const isYouTube = isVideo && mediaUrl ? isYouTubeUrl(mediaUrl) : false;
  const canDownload = !isVideo && !!mediaUrl;

  const handleFullscreen = () => {
    if (mediaRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        mediaRef.current.requestFullscreen();
      }
    }
  };

  const handlePiP = async () => {
    if (!isVideo || !mediaRef.current) return;
    try {
      const videoEl = mediaRef.current as HTMLVideoElement;
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else {
        await videoEl.requestPictureInPicture();
      }
    } catch (e) { console.error(e); }
  };

  const toggleMute = () => {
    changeVolume(volume === 0 ? 0.8 : 0);
  };

  const checkDownloadStatus = async () => {
    if (!canDownload || !mediaUrl || !('caches' in window)) return;
    const cache = await caches.open(MEDIA_CACHE_NAME);
    const match = await cache.match(mediaUrl);
    setDownloadState(match ? 'done' : 'idle');
  };

  const handleDownload = async () => {
    if (!canDownload || !mediaUrl || !('caches' in window)) return;
    setDownloadState('downloading');
    try {
      const cache = await caches.open(MEDIA_CACHE_NAME);
      const response = await fetch(mediaUrl, { mode: 'cors' });
      if (!response.ok) throw new Error('Download failed');
      await cache.put(mediaUrl, response.clone());
      setDownloadState('done');
    } catch (err) {
      setDownloadState('error');
    }
  };

  useEffect(() => {
    setDownloadState('idle');
    checkDownloadStatus();
  }, [mediaUrl, canDownload]);

  if (!currentEpisode) return null;

  if (isMinimized) {
    return (
      <div className="fixed bottom-16 md:bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-xl h-20 z-50 flex items-center px-4 justify-between animate-in slide-in-from-bottom">
        <div className="absolute top-0 left-0 h-1 bg-brand-100 w-full"><div className="h-full bg-brand-500" style={{ width: `${(progress / duration) * 100}%` }} /></div>
        <div className="flex items-center gap-4 flex-1 cursor-pointer overflow-hidden" onClick={toggleMinimize}>
          <img src={currentEpisode.thumbnail} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
          <div className="flex flex-col min-w-0"><span className="text-sm font-black text-brand-900 truncate tracking-tight">{currentEpisode.title}</span></div>
        </div>
        <div className="flex items-center gap-2">
          {!isYouTube && (
            <button onClick={(e) => { e.stopPropagation(); isPlaying ? pause() : resume(); }} className="w-10 h-10 bg-brand-900 text-white rounded-full flex items-center justify-center">
              {isPlaying ? <Pause size={18} weight="fill" /> : <Play size={18} weight="fill" className="ml-0.5" />}
            </button>
          )}
          <button onClick={(e) => { e.stopPropagation(); closePlayer(); }} className="p-2 text-gray-400"><X size={20} weight="bold" /></button>
        </div>
        <audio ref={mediaRef as any} src={(isVideo || isYouTube) ? undefined : (mediaUrl || undefined)} className="hidden" />
        {isVideo && !isYouTube && <video ref={mediaRef as any} src={mediaUrl || undefined} className="hidden" playsInline />}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col animate-in slide-in-from-bottom duration-500 overflow-hidden">
      {/* Top Nav */}
      <div className="flex items-center justify-between p-6">
        <button onClick={toggleMinimize} className="p-2 text-gray-400 hover:text-brand-900"><CaretDown size={32} weight="bold" /></button>
          <div className="text-center">
          <span className="text-[10px] font-black uppercase tracking-widest text-brand-500 mb-0.5 block">Nida Studios Original</span>
          <span className="text-sm font-bold text-brand-900">{isVideo ? 'Visual Documentary' : 'Sonic Insight'}</span>
        </div>
        <div className="w-12"></div> {/* Settings Icon Removed */}
      </div>

      {/* Media Display */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 pb-12 overflow-y-auto no-scrollbar">
        {isVideo ? (
          <div
            className={`w-full ${isCinematic ? 'max-w-7xl' : 'max-w-3xl'} aspect-video bg-black rounded-3xl overflow-hidden shadow-2xl mb-8 relative group transition-all duration-700`}
            onMouseEnter={() => setIsHoveringVideo(true)}
            onMouseLeave={() => setIsHoveringVideo(false)}
          >
            {isYouTube ? (
              <iframe
                src={getYouTubeEmbedUrl(mediaUrl || '') || ''}
                className="w-full h-full border-none"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <>
                <video ref={mediaRef as any} src={mediaUrl || undefined} className="w-full h-full object-contain cursor-pointer" onClick={isPlaying ? pause : resume} playsInline />

                {/* Overlay Controls */}
                <div className={`absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent transition-opacity duration-300 ${isHoveringVideo || !isPlaying ? 'opacity-100' : 'opacity-0'}`}>
                  <div className="flex items-center gap-4 mb-4">
                    <button onClick={toggleMute} className="text-white hover:text-brand-500 transition-colors">
                      {volume === 0 ? <SpeakerSlash size={24} weight="fill" /> : <SpeakerHigh size={24} weight="fill" />}
                    </button>
                    <input
                      type="range" min="0" max="1" step="0.01" value={volume}
                      onChange={(e) => changeVolume(parseFloat(e.target.value))}
                      className="w-24 accent-white h-1 cursor-pointer"
                    />

                    <div className="flex-1"></div>

                    {/* Integrated Playback Rate in Overlay */}
                    <div className="relative">
                      <button
                        onClick={() => setShowRateMenu(!showRateMenu)}
                        className="text-white text-xs font-black uppercase tracking-widest px-2 py-1 bg-white/10 rounded-md hover:bg-white/20 transition-colors"
                      >
                        {playbackRate}x
                      </button>
                      {showRateMenu && (
                        <div className="absolute bottom-full right-0 mb-2 bg-white rounded-lg shadow-2xl py-2 w-20 overflow-hidden">
                          {[0.5, 1, 1.25, 1.5, 2].map(rate => (
                            <button
                              key={rate}
                              onClick={() => { changePlaybackRate(rate); setShowRateMenu(false); }}
                              className={`w-full px-4 py-2 text-xs font-bold text-left hover:bg-gray-100 ${playbackRate === rate ? 'text-brand-500' : 'text-gray-600'}`}
                            >
                              {rate}x
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <button onClick={() => setIsCinematic(!isCinematic)} className="text-white hover:text-brand-500 transition-colors" title="Toggle Cinematic Mode">
                      {isCinematic ? <CornersIn size={20} weight="bold" /> : <Television size={20} weight="bold" />}
                    </button>
                    <button onClick={handlePiP} className="text-white hover:text-brand-500 transition-colors" title="Picture in Picture">
                      <Lightning size={20} weight="bold" />
                    </button>
                    <button onClick={handleFullscreen} className="text-white hover:text-brand-500 transition-colors" title="Fullscreen">
                      <CornersOut size={20} weight="bold" />
                    </button>
                  </div>
                  <div className="h-1.5 bg-white/20 rounded-full overflow-hidden cursor-pointer" onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    seek(((e.clientX - rect.left) / rect.width) * duration);
                  }}>
                    <div className="h-full bg-brand-500" style={{ width: `${(progress / duration) * 100}%` }} />
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="relative w-full max-w-sm aspect-square rounded-3xl overflow-hidden shadow-2xl mb-12 transform group-hover:scale-105 transition-transform duration-500">
            <img src={currentEpisode.thumbnail} className="w-full h-full object-cover" />
            <div className="absolute inset-x-6 bottom-6 h-10 rounded-xl bg-white/10 backdrop-blur-sm overflow-hidden">
              <div className="h-full w-full bg-[repeating-linear-gradient(90deg,rgba(255,255,255,0.7)_0,rgba(255,255,255,0.7)_2px,transparent_2px,transparent_7px)] animate-pulse" />
            </div>
            <audio ref={mediaRef as any} src={mediaUrl || undefined} />
          </div>
        )}

        <div className="w-full max-w-2xl text-center mb-10">
          <h2 className="text-3xl md:text-5xl font-serif font-black text-brand-900 mb-3 tracking-tight leading-tight">{currentEpisode.title}</h2>
          <span className="text-brand-500 font-bold uppercase text-[10px] tracking-widest bg-brand-50 px-3 py-1 rounded-full">Episode {currentEpisode.episodeNumber || 'Special'}</span>
          {canListenOnly && (
            <div className="mt-4">
              <button
                onClick={() => setForceAudioOnly(!forceAudioOnly)}
                className="text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full border border-gray-200 text-gray-500 hover:text-brand-900 hover:border-brand-500 transition-colors"
              >
                {forceAudioOnly ? 'Watch Video' : 'Listen Only'}
              </button>
            </div>
          )}
        </div>

        {/* Controls Bar */}
        {!isYouTube && (
          <div className="w-full max-w-2xl space-y-8">
            {!isVideo && (
              <div className="w-full group cursor-pointer" onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                seek(((e.clientX - rect.left) / rect.width) * duration);
              }}>
                <div className="h-2 bg-gray-100 rounded-full mb-3 overflow-hidden">
                  <div className="h-full bg-brand-900" style={{ width: `${(progress / duration) * 100}%` }} />
                </div>
                <div className="flex justify-between text-[10px] font-black text-gray-400 font-mono tracking-widest">
                  <span>{formatTime(progress)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>
            )}

            <div className="flex items-center justify-center gap-12">
              <button onClick={() => seek(progress - 15)} className="text-gray-300 hover:text-brand-900 transition-colors"><SkipBack size={36} weight="fill" /></button>
              <button onClick={isPlaying ? pause : resume} className="w-24 h-24 bg-brand-900 text-white rounded-full flex items-center justify-center shadow-2xl hover:scale-105 active:scale-95 transition-all">
                {isPlaying ? <Pause size={40} weight="fill" /> : <Play size={40} weight="fill" className="ml-1.5" />}
              </button>
              <button onClick={() => seek(progress + 30)} className="text-gray-300 hover:text-brand-900 transition-colors"><SkipForward size={36} weight="fill" /></button>
            </div>

            {canDownload && (
              <div className="flex items-center justify-center">
                <button
                  onClick={handleDownload}
                  disabled={downloadState === 'downloading' || downloadState === 'done'}
                  className="px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest border border-gray-200 text-gray-600 hover:text-brand-900 hover:border-brand-500 transition-colors disabled:opacity-50"
                >
                  {downloadState === 'downloading' && 'Downloading...'}
                  {downloadState === 'done' && 'Downloaded'}
                  {downloadState === 'error' && 'Retry Download'}
                  {downloadState === 'idle' && 'Download for Offline'}
                </button>
              </div>
            )}

            {/* Volume and Playback Rate Control Integrated into main view */}
            <div className="flex flex-col md:flex-row items-center justify-center gap-8">
              <div className="flex items-center gap-4 text-gray-400">
                <button onClick={toggleMute} className="hover:text-brand-900 transition-colors">
                  {volume === 0 ? <SpeakerSlash size={20} weight="fill" /> : <SpeakerHigh size={20} weight="fill" />}
                </button>
                <input
                  type="range" min="0" max="1" step="0.01" value={volume}
                  onChange={(e) => changeVolume(parseFloat(e.target.value))}
                  className="w-32 accent-brand-900 h-1 cursor-pointer"
                />
              </div>

              <div className="flex gap-2">
                {[1, 1.25, 1.5, 2].map(rate => (
                  <button
                    key={rate}
                    onClick={() => changePlaybackRate(rate)}
                    className={`px-3 py-1 rounded text-[10px] font-black tracking-widest transition-colors ${playbackRate === rate ? 'bg-brand-900 text-white' : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-brand-900'}`}
                  >
                    {rate}x
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const formatTime = (seconds: number) => {
  if (!seconds || isNaN(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  return `${mins}:${Math.floor(seconds % 60).toString().padStart(2, '0')}`;
};
